import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from "react";

import { ApiError } from "@tdelivr/api-client";
import type { Transport } from "@tdelivr/api-client";
import { createDispatchConsoleBffClient } from "@tdelivr/api-client/generated/experience/dispatch-console-bff.js";
import type {
  AreaView,
  DemandRow as DemandRowView,
  FenceView,
  LiveMapView,
  RosterDriver,
  SourceReport,
} from "@tdelivr/api-client/generated/experience/dispatch-console-bff.js";

// The queue's own vocabulary, borrowed rather than restated. `finding_candidates` is a good name
// for a saga and a poor one for somebody deciding whether to intervene, and this console has
// already settled on one set of words for DSP-05's five statuses — two screens showing the same
// dispatch under two different names would be worse than either name.
import { readableStatus } from "../demand-queue.ts";
import {
  DEMAND_EXHAUSTED,
  DEMAND_PINS,
  DRIVER_MARKERS,
  driverGlyph,
  fencePoints,
  jobLine,
  ROUTE_LOOKS,
  type JobLine,
  formatAge,
  formatClock,
  formatLastFix,
  markerKeyFor,
  projectionFor,
  type Projection,
  type Coordinate,
  type MarkerKey,
} from "../live-map.ts";
import {
  boundsOf,
  loadGoogleMaps,
  MAP_OPTIONS,
  onMapsAuthFailure,
  projectionFromMap,
  tilesAreConfigured,
  type GoogleMap,
} from "../google-maps.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * The dispatch live map: everything happening in the operating area, at once.
 *
 * Built to `05 Dispatch Live Map.dc.html`. Its surface is drawn the way the design draws it —
 * absolutely-positioned elements over two CSS gradients, no canvas, no mapping library and no tile
 * request to anybody. That is not a shortcut taken here: the design's own footer says
 * *"Map provider open · vendor-neutral surface"*, and rendering the file confirms it makes no
 * network request except for the two Google Fonts stylesheets every design file loads. Adding a
 * tile source would be choosing a map vendor on a screen whose design deliberately has not.
 *
 * **One request, one scope, one instant.** This screen made seven requests across twelve read
 * scopes from the browser and joined their rows here. R-09 measured why that was wrong and
 * [ADR 0020](../../../../docs/adr/0020-the-experience-tier-is-shaped-like-a-service.md) built the
 * answer: EXP-008 composes the eight layers server-side, with the operator's own access token, and
 * publishes one verdict per layer in `sources` whether that layer answered or not. So this page
 * reads `dispatch-console:read` and nothing else, the roster arrives joined, the service-area ring
 * arrives latitude-first, and a geofence service restarting still costs one layer rather than the
 * page — the same property as before, now stated by the response instead of inferred from seven
 * settled promises. R-14 records the change.
 *
 * **What is drawn that was not, and what still is not.** The demand pins are on the surface for the
 * first time: the aggregate makes the MOB-03 read per dispatch that R-09 refused to make in a
 * browser, so `demand[].origin` is a real pickup where it is readable and `null` with a reason
 * where it is not. Still absent, because nothing publishes them: a trip's destination, a route
 * geometry outside TRP-02's by-trip read, and any join from a driver to a vehicle — so the
 * destination pins, the route lines and the `local_taxi` / `two_wheeler` split are missing rather
 * than invented. Everything the platform does publish is drawn exactly as designed.
 *
 * **One driver marker is drawn differently from the design, deliberately.** A driver the platform
 * only *infers* is working carries a dashed ring where a confirmed trip carries a solid one. R-09's
 * over-claim was invisible before because the screen had one flag; the aggregate now publishes what
 * the belief rests on, and `markerKeyFor` in `live-map.ts` holds the treatment and the reasoning.
 * Q-25 records that design rule 5 names four markers and this is a fifth.
 */

/** The five layers, in the design's order, with its words and its glyphs. */
const LAYERS = [
  { key: "drivers", label: "Drivers", glyph: "directions_car" },
  { key: "demand", label: "Demand", glyph: "pending_actions" },
  { key: "jobs", label: "Jobs", glyph: "route" },
  { key: "fence", label: "Geofences", glyph: "polyline" },
  { key: "area", label: "Service area", glyph: "map" },
] as const;

type LayerKey = (typeof LAYERS)[number]["key"];

/** The legend, always on the map rather than hidden in a menu, in the design's order and words. */
const LEGEND = [
  { key: "available", label: "Available" },
  { key: "job", label: "On a job" },
  { key: "stale", label: "Stale location" },
  { key: "demand-waiting", label: "Demand waiting" },
  { key: "demand-failing", label: "Demand failing" },
  { key: "destination", label: "Job destination" },
] as const;

/**
 * The legend keys whose colour is one of design rule 5's two literal marker fills.
 *
 * The other four keys are entirely token-coloured and take their whole appearance from the
 * stylesheet. These two cannot, for the reason the stylesheet's own header gives, so they read the
 * fill from the same table the markers read — the design draws the on-a-job key as a disc half in
 * the marker's blue and half in the surface, which is the same half-filled shape the `active` state
 * badge uses, so it is a shape as well as a colour here too.
 */
const LEGEND_KEY_STYLES: Partial<Record<(typeof LEGEND)[number]["key"], CSSProperties>> = {
  available: { background: DRIVER_MARKERS.available.fill ?? undefined },
  job: {
    borderColor: DRIVER_MARKERS.job.fill ?? undefined,
    background: `linear-gradient(90deg, ${DRIVER_MARKERS.job.fill ?? "transparent"} 50%, var(--surface) 50%)`,
  },
};

type TabKey = "queue" | "jobs" | "drivers";

type Selection =
  | { readonly type: "driver"; readonly id: string }
  | { readonly type: "demand"; readonly id: string }
  | { readonly type: "job"; readonly id: string };

/**
 * One job in the Jobs tab.
 *
 * Rebuilt from the roster rather than read as a layer of its own, because the aggregate publishes
 * no trips array: a trip reaches this screen as `tripId` on the driver running it, which is the
 * only part of TRP-02 the live map ever drew. The lifecycle is `active` for every one of them by
 * construction — the aggregate reads TRP-02's active trips — where the screen used to filter the
 * listing itself. R-14 records what that costs: a trip whose driver DRV-05 reports offline is not
 * in the roster, so it is not in this tab either.
 */
interface Job {
  readonly tripId: string;
  readonly driverId: string;
  readonly driverName: string;
}

/**
 * The eight layers the aggregate reports on, by the names it gives them.
 *
 * Not a vocabulary of this screen's: every one of these strings is a `SourceReport.layer` value
 * published by EXP-008, and a report whose layer is not one of them is ignored rather than guessed
 * at.
 */
const LAYERS_REPORTED = [
  "driverLocations",
  "driverProfiles",
  "driverAvailability",
  "trips",
  "demand",
  "demandOrigins",
  "serviceAreas",
  "geofences",
] as const;

type ReportedLayer = (typeof LAYERS_REPORTED)[number];

/** One verdict per layer, as a state a panel can render, with `undefined` meaning it answered. */
type LayerStates = Readonly<Partial<Record<ReportedLayer, ViewState>>>;

/** What one request to the aggregate left the screen holding. */
interface Snapshot {
  /** The aggregate, when there is one. */
  readonly view?: LiveMapView;
  /** Why there is none: the single request itself was refused or failed. */
  readonly failure?: ViewState;
  /**
   * The per-layer verdicts the refusal carried, where it carried any.
   *
   * The aggregate answers 403 rather than 200 when *every* layer was refused, and it puts the eight
   * reports in `details.sources` — so even that refusal says which permission each layer wanted,
   * and the panels can each name their own instead of all repeating one sentence.
   */
  readonly refused?: readonly SourceReport[];
}

/** Nothing has come back yet, which is not the same as nothing being there. */
const PENDING: Snapshot = {};

/** No aggregate, so no rows of any kind — but the arrays still have to exist to be mapped over. */
const NO_DRIVERS: readonly RosterDriver[] = [];
const NO_DEMAND: readonly DemandRowView[] = [];
const NO_AREAS: readonly AreaView[] = [];
const NO_FENCES: readonly FenceView[] = [];

/**
 * One layer's verdict as a state this console already knows how to draw.
 *
 * The same six categories `stateForError` maps an `ApiError` onto, read off the report instead of
 * off a throw — because that is what the aggregate turned the throw into. The two authorization
 * statuses become `forbidden` carrying the scopes the report names, which is what makes the
 * forbidden state say *which* permission is missing on a screen that never saw the 403 itself.
 * `unavailable` and `timeout` are retryable and `invalid` is not, which is the fan-out's own
 * distinction: an answer this aggregate cannot read will read the same way next time.
 *
 * `offline` is false in every case. The request reached the platform — the aggregate answered —
 * so saying the console cannot reach it would be false in a way an operator would act on.
 */
function stateForReport(report: SourceReport): ViewState | undefined {
  switch (report.status) {
    case "ok":
      return undefined;
    case "forbidden":
    case "unauthorized":
      return { kind: "forbidden", missingScopes: report.scopes };
    case "unavailable":
    case "timeout":
      return {
        kind: "error",
        code: report.code ?? `${report.layer}_${report.status}`,
        message:
          report.status === "timeout"
            ? `${report.service} did not answer in time`
            : `${report.service} did not answer`,
        retryable: true,
        offline: false,
      };
    case "invalid":
      return {
        kind: "error",
        code: report.code ?? `${report.layer}_invalid`,
        message: `${report.service} answered something the console cannot read`,
        retryable: false,
        offline: false,
      };
  }
}

/**
 * Which panels have lost what.
 *
 * Three cases, and the middle one is the reason this is a function rather than a line.
 *
 *   - The aggregate answered: each panel takes its own layer's verdict.
 *   - It refused and said which layers were refused: each panel still takes its own, so an operator
 *     missing `geo:geofence:read` and `trip-tracking:read` is told both by name rather than being
 *     told eight times that they cannot see the page.
 *   - It refused without saying, or could not be reached: the one failure belongs to every panel,
 *     because there is no aggregate and no panel has data. Each saying the same true thing is
 *     better than one saying it and the rest rendering an empty list.
 */
function layerStates(snapshot: Snapshot): LayerStates {
  const reports = snapshot.refused ?? snapshot.view?.sources;
  const states: Partial<Record<ReportedLayer, ViewState>> = {};
  if (snapshot.failure !== undefined && (reports === undefined || reports.length === 0)) {
    for (const layer of LAYERS_REPORTED) states[layer] = snapshot.failure;
    return states;
  }
  for (const report of reports ?? []) {
    if (!(LAYERS_REPORTED as readonly string[]).includes(report.layer)) continue;
    const state = stateForReport(report);
    if (state !== undefined) states[report.layer as ReportedLayer] = state;
  }
  return states;
}

/**
 * The layer verdicts a refusal carried, if it carried any.
 *
 * Read defensively rather than cast. These come off an error body, so the shape is whatever arrived
 * — and a report with no `layer`, no `status` or no `scopes` is dropped rather than rendered as a
 * panel claiming an unnamed permission is missing.
 */
function reportsIn(error: unknown): readonly SourceReport[] | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const sources = error.details?.["sources"];
  if (!Array.isArray(sources)) return undefined;
  const reports: SourceReport[] = [];
  for (const entry of sources as readonly unknown[]) {
    if (typeof entry !== "object" || entry === null) continue;
    const record = entry as Record<string, unknown>;
    const { layer, service, status, scopes } = record;
    if (typeof layer !== "string" || typeof service !== "string") continue;
    if (typeof status !== "string" || !Array.isArray(scopes)) continue;
    reports.push({
      layer,
      service,
      status: status as SourceReport["status"],
      scopes: scopes.filter((scope): scope is string => typeof scope === "string"),
      ...(typeof record["code"] === "string" ? { code: record["code"] } : {}),
    });
  }
  return reports.length === 0 ? undefined : reports;
}

/**
 * Which scope to name when the one request itself was refused and said nothing more.
 *
 * `dispatch-console:read`, and only here. Two different 403s reach this screen and naming the wrong
 * one sends an operator to ask for a permission they already hold: `insufficient_scope` means the
 * gate was refused, and that is this scope; every-layer-refused means the gate was *passed* and the
 * eight owning services each declined the forwarded token, which is ADR 0020 working exactly as
 * designed. The second case carries its own reports, so it never gets here.
 */
const GATE_SCOPE = ["dispatch-console:read"] as const;

export interface LiveMapProps {
  readonly transport: Transport;
  readonly tenantId: string;
  /** Innermost last: tenant, section, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /** Injected so a countdown can be exercised without waiting for a deadline to pass. */
  readonly now?: () => number;
}

/**
 * The design's three frame widths, as media queries.
 *
 * `05 Dispatch Live Map.dc.html` measures its own frame and switches at 1240, 900 and 560: at 1240
 * the roster joins the queue and the map, below 900 everything stacks with the map first, and below
 * 560 the selection card leaves the map and falls in below it. The frame there is the whole console
 * including its rail, which is what a viewport width is here, so the thresholds transfer unchanged.
 *
 * The layout itself is CSS; these two booleans exist only for the things CSS cannot decide — which
 * tabs exist, and whether the selection card is drawn over the map or under it.
 */
function useFrameWidth(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return (): void => undefined;
      }
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return (): void => {
        list.removeEventListener("change", onChange);
      };
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(query).matches,
    // Rendered on a server, nothing is wide. The console is client-rendered; this is only so the
    // hook is honest about a case it cannot answer rather than throwing in one.
    () => false,
  );
}

/**
 * The surface's measured size, because the design's job line cannot be computed without it.
 *
 * The design reads `#mapbox`'s `clientWidth` and `clientHeight` for exactly this, and the
 * reason is geometric rather than cosmetic: the two endpoints are percentages of a box that is
 * wider than it is tall, so the angle between them in percent is not the angle on screen. `jobLine`
 * converts to pixels first. Nothing else on this surface needs it — a marker is placed by
 * percentages alone, which is why this arrives with the first line rather than with the map.
 *
 * Zero until measured, and a zero-length line is not drawn. A `ResizeObserver` rather than a
 * window resize listener, because this box also changes size when the side panel's tab changes
 * and when the drawer opens below 760, neither of which resizes the window.
 */
function useSurfaceSize(): {
  readonly ref: (node: HTMLDivElement | null) => void;
  readonly size: { readonly width: number; readonly height: number };
} {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const observer = useRef<ResizeObserver | undefined>(undefined);

  const ref = useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect();
    if (node === null) return;
    // Measured once on attach as well as on change: an observer fires on connect in every browser
    // that has one, but a jsdom test has no ResizeObserver at all and this is what it reads.
    setSize({ width: node.clientWidth, height: node.clientHeight });
    if (typeof ResizeObserver !== "function") return;
    observer.current = new ResizeObserver(() => {
      setSize({ width: node.clientWidth, height: node.clientHeight });
    });
    observer.current.observe(node);
  }, []);

  useEffect(
    () => (): void => {
      observer.current?.disconnect();
    },
    [],
  );

  return { ref, size };
}

export function LiveMap({ transport, tenantId, crumbs, now }: LiveMapProps): ReactNode {
  const [snapshot, setSnapshot] = useState<Snapshot>(PENDING);

  const [layers, setLayers] = useState<Readonly<Record<LayerKey, boolean>>>({
    drivers: true,
    demand: true,
    jobs: true,
    fence: true,
    area: true,
  });
  const [tab, setTab] = useState<TabKey>("queue");
  const [selection, setSelection] = useState<Selection | undefined>(undefined);

  const wide = useFrameWidth("(min-width: 1240px)");
  const tiny = useFrameWidth("(max-width: 559px)");

  const load = useCallback(async () => {
    /*
     * One read.
     *
     * There is no `allSettled` here any more and no array to index into, because there is one
     * request: the eight layers are settled inside EXP-008, which never lets one of them fail the
     * aggregate and publishes a verdict for each. What this `catch` handles is the case the
     * fan-out cannot — the aggregate itself refusing (no `dispatch-console:read`, or a token bound
     * to another tenant) or being unreachable, which is one failure rather than seven.
     */
    try {
      const answer =
        await createDispatchConsoleBffClient(transport).getDispatchConsoleLiveMap(tenantId);
      setSnapshot({ view: answer });
    } catch (error) {
      const refused = reportsIn(error);
      setSnapshot({
        failure: stateForError(error, GATE_SCOPE),
        ...(refused === undefined ? {} : { refused }),
      });
    }
  }, [transport, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const view = snapshot.view;
  const loaded = snapshot.view !== undefined || snapshot.failure !== undefined;
  const states = useMemo(() => layerStates(snapshot), [snapshot]);

  /*
   * Taken as given, in the order and with the membership the aggregate chose.
   *
   * No sort, no filter and no join. The roster is stalest-first because DRV-05 orders it that way
   * and the aggregate keeps that order; the queue is soonest-deadline-first for the same reason;
   * offline drivers are already out, counted in `counts.offline` so the exclusion is visible
   * rather than silent. A screen re-sorting any of it would be replacing an ordering it does not
   * own, which is the argument `demand-queue.ts` makes about DSP-05's queue.
   */
  const roster = view?.drivers ?? NO_DRIVERS;
  const queue = view?.demand ?? NO_DEMAND;
  const areas = view?.serviceAreas ?? NO_AREAS;
  const fences = view?.geofences ?? NO_FENCES;
  const counts = view?.counts;

  /*
   * The Jobs tab, from the drivers running the trips.
   *
   * The aggregate publishes no trips list — a trip arrives as `tripId` on its driver — so this is
   * the whole of what the tab can show, which is what it showed before: the identifier, the driver
   * and the lifecycle. Every one of them is active, because the aggregate reads TRP-02's active
   * trips.
   */
  const jobs = useMemo(
    (): Job[] =>
      roster
        .filter(
          (driver): driver is RosterDriver & { tripId: string } => driver.tripId !== undefined,
        )
        .map((driver) => ({
          tripId: driver.tripId,
          driverId: driver.driverId,
          driverName: driver.displayName ?? driver.driverId,
        })),
    [roster],
  );

  /*
   * One projection over everything that has a coordinate.
   *
   * Fitted to the drivers, the demand pickups, the fences and the service areas together, so a
   * driver outside the service area is drawn outside it rather than clamped into it. Refitting on
   * every repaint would make the surface jump every time one driver moves, so this is memoised on
   * the data rather than recomputed on each render — the view still re-fits when the set of things
   * on it changes, which is the behaviour a bounded operating area wants.
   *
   * The demand pickups are new here, and they belong in the fit rather than only on the surface: a
   * dispatch waiting outside the area the drivers are in is the case an operator most needs to see
   * whole, and a pin fitted to a box drawn without it would sit on the edge.
   */
  const surface = useSurfaceSize();

  const points = useMemo(() => {
    const found: Coordinate[] = [];
    for (const driver of roster) if (driver.fix) found.push(driver.fix);
    // A job destination belongs in the fit for the reason a demand pickup does: a line that leaves
    // the fitted box is a line drawn to the edge, pointing at a pin that is not there.
    for (const driver of roster) if (driver.tripDestination) found.push(driver.tripDestination);
    for (const row of queue) if (row.origin !== null) found.push(row.origin);
    for (const fence of fences) found.push(...fencePoints(fence));
    for (const area of areas) found.push(...area.ring);
    return found;
  }, [roster, queue, fences, areas]);

  const fitted = useMemo(() => projectionFor(points), [points]);

  /*
   * Tiles, when a key is configured, and the box the design draws when one is not.
   *
   * ADR 0099's own consequence — *"the live map stops being a box"* — with the keyless path kept
   * exactly as it was. That is not caution: the design file draws an empty surface too, so
   * `check-live-map.mts` compares this build against it, and tiles in the keyless render would
   * make that check fail for ever against a design that has no tiles. Every test also runs in
   * jsdom with no key, and a console that cannot draw its main screen for want of a billing
   * account is worse than one that draws a schematic.
   */
  const tileHost = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<GoogleMap | undefined>(undefined);
  const [mapProjection, setMapProjection] = useState<Projection | undefined>(undefined);
  const [tilesRefused, setTilesRefused] = useState(false);

  useEffect(() => {
    if (!tilesAreConfigured()) return undefined;
    const host = tileHost.current;
    if (host === null) return undefined;

    let live = true;
    let listener: { remove(): void } | undefined;

    /*
     * A key refused for its referrer fails here rather than at load — Google checks it when
     * the map asks for a viewport — and without this hook it paints its own grey panel over
     * the board. Dropping the projection puts every marker back on the fitted one over the
     * drawn surface, which is the screen a dispatcher can still work from.
     */
    const unregister = onMapsAuthFailure(() => {
      if (!live) return;
      setMapProjection(undefined);
      setTilesRefused(true);
    });

    void loadGoogleMaps()
      .then((google) => {
        if (!live) return;
        const instance = new google.maps.Map(host, { ...MAP_OPTIONS });
        /*
         * Re-read the projection whenever the view settles. `idle` rather than `bounds_changed`,
         * which fires on every frame of a pan: the markers would be re-projected sixty times a
         * second against a view still moving, and they move with the tiles anyway because the
         * whole surface is one element.
         */
        listener = google.maps.event.addListener(instance, "idle", () => {
          setMapProjection(projectionFromMap(instance));
        });
        setMap(instance);
      })
      .catch((error: unknown) => {
        /*
         * A key rejected for its referrer, a blocked script, an offline console. The board keeps
         * the fitted projection and the drawn surface, which is a working screen — so this is
         * reported and not raised.
         */
        console.warn("Live map tiles unavailable; drawing the schematic surface", error);
      });

    return (): void => {
      live = false;
      listener?.remove();
      unregister();
    };
  }, []);

  /* Frame every point the board is drawing, the way the fitted projection does. */
  useEffect(() => {
    if (map === undefined) return;
    const google = (globalThis as { google?: Parameters<typeof boundsOf>[0] }).google;
    if (google === undefined) return;
    const bounds = boundsOf(google, points);
    if (bounds !== undefined) map.fitBounds(bounds, 48);
  }, [map, points]);

  /*
   * The map's projection where there is one, the fit otherwise.
   *
   * Every marker on this board goes through `projection.place`, so this one line is the whole
   * of the integration: the marker layer asks for percentages of the surface and gets them, and
   * what changed is where the percentages come from.
   */
  const projection = mapProjection ?? fitted;

  const nowMs = now?.() ?? Date.now();

  /*
   * One state for the surface, from the layer that is the surface's reason to exist.
   *
   * The drivers are that layer. A map with fences and no drivers is a configuration screen; a map
   * with drivers and no fences is still a live map, so the other six failures are shown beside the
   * data rather than instead of it.
   */
  const surfaceState: ViewState =
    states.driverLocations ?? (loaded ? stateForList(roster) : { kind: "loading" });

  const selected = useSelection(selection, { roster, queue, jobs, nowMs });

  const card =
    selected === undefined ? null : (
      <SelectionCard
        detail={selected}
        onClose={() => {
          setSelection(undefined);
        }}
      />
    );

  return (
    <section className="page live-map">
      <PageHeading
        crumbs={crumbs ?? []}
        title="Live map"
        /*
         * One code, because one service is read. The design's chip names none — its top bar carries
         * the tenant instead — but every other page in this console names what it reads, and this
         * page now reads EXP-008 and nothing else.
         *
         * The eight services behind it are not lost from the screen: each one names itself in its
         * own `SourceReport`, and `LayerFailures` at the foot prints the ones that did not answer.
         * That is strictly better than the seven codes this chip used to carry, which said what
         * *could* be wrong rather than what is.
         */
        source="EXP-008"
        description="Everything happening in the operating area, at once."
        aside={
          /*
           * The design puts this pill in the top bar, beside the layer switches. The shell owns the
           * top bar in this console and draws neither, so both are page content here. R-09.
           */
          <span className="live-map__live">
            <span aria-hidden="true" className="live-map__live-dot" />
            Live
          </span>
        }
      />

      <div className="live-map__layers" role="group" aria-label="Map layers">
        {LAYERS.map((layer) => (
          <button
            key={layer.key}
            type="button"
            aria-pressed={layers[layer.key]}
            className={
              layers[layer.key] ? "live-map__layer live-map__layer--on" : "live-map__layer"
            }
            onClick={() => {
              setLayers((current) => ({ ...current, [layer.key]: !current[layer.key] }));
            }}
          >
            <span aria-hidden="true" className="live-map__layer-glyph">
              {layer.glyph}
            </span>
            <span className="live-map__layer-label">{layer.label}</span>
          </button>
        ))}
      </div>

      <div className="live-map__frame">
        <section className="live-map__side" aria-label="Queue and jobs">
          <div className="live-map__tabs" role="tablist">
            <Tab
              tab="queue"
              label="Demand"
              count={queue.length}
              current={tab}
              onPick={setTab}
              wide={wide}
            />
            <Tab
              tab="jobs"
              label="Jobs"
              count={jobs.length}
              current={tab}
              onPick={setTab}
              wide={wide}
            />
            <Tab
              tab="drivers"
              label="Drivers"
              count={roster.length}
              current={tab}
              onPick={setTab}
              wide={wide}
            />
          </div>

          <div className="live-map__list">
            {tab === "queue" ? (
              <StateView
                state={states.demand ?? (loaded ? stateForList(queue) : { kind: "loading" })}
                purpose="Requests waiting for a driver appear here as soon as the platform starts looking."
                onRetry={() => {
                  void load();
                }}
              >
                {queue.map((row) => (
                  <DemandRow
                    key={row.demandId}
                    row={row}
                    nowMs={nowMs}
                    current={selection?.type === "demand" && selection.id === row.demandId}
                    onPick={() => {
                      setSelection({ type: "demand", id: row.demandId });
                    }}
                  />
                ))}
              </StateView>
            ) : tab === "jobs" ? (
              <StateView
                state={states.trips ?? (loaded ? stateForList(jobs) : { kind: "loading" })}
                purpose="Trips currently running appear here."
                onRetry={() => {
                  void load();
                }}
              >
                {jobs.map((job) => (
                  <JobRow
                    key={job.tripId}
                    job={job}
                    current={selection?.type === "job" && selection.id === job.tripId}
                    onPick={() => {
                      setSelection({ type: "job", id: job.tripId });
                    }}
                  />
                ))}
              </StateView>
            ) : (
              <StateView
                state={surfaceState}
                purpose="Drivers who are online appear here."
                onRetry={() => {
                  void load();
                }}
              >
                {roster.map((driver) => (
                  <DriverRow
                    key={driver.driverId}
                    driver={driver}
                    current={selection?.type === "driver" && selection.id === driver.driverId}
                    onPick={() => {
                      setSelection({ type: "driver", id: driver.driverId });
                    }}
                  />
                ))}
              </StateView>
            )}
          </div>
        </section>

        <div className="live-map__map" ref={surface.ref}>
          {/* Where the tiles mount, and nothing at all without a key. Behind every marker, and
              hidden from assistive technology for the reason the decoration below is: a map image
              tells a screen reader nothing the markers and the panels do not say better. */}
          {tilesAreConfigured() && !tilesRefused ? (
            <div aria-hidden="true" className="live-map__tiles" ref={tileHost} />
          ) : null}

          {/* The surface. Every element below is decoration the design draws, and every one of them
              is hidden from assistive technology exactly as the design hides it: the information is
              in the markers and in the panels beside the map, never in the backdrop.

              Drawn only without tiles: over real roads these four divs are four grey bars across
              Nairobi. */}
          {tilesAreConfigured() ? null : (
            <>
              <div aria-hidden="true" className="live-map__ground" />
              <div aria-hidden="true" className="live-map__road live-map__road--a" />
              <div aria-hidden="true" className="live-map__road live-map__road--b" />
              <div aria-hidden="true" className="live-map__road live-map__road--c" />
              <div aria-hidden="true" className="live-map__road live-map__road--d" />
              <div aria-hidden="true" className="live-map__park" />
            </>
          )}

          {layers.area
            ? areas.map((area) => {
                // The ring arrives latitude-first and already checked pair by pair; a boundary that
                // is a geofence reference arrives empty, and is drawn from the fence layer instead.
                if (area.ring.length < 3) return null;
                const placed = area.ring.map((point) => projection.place(point));
                const anchor = placed.reduce((best, point) =>
                  point.top < best.top ? point : best,
                );
                return (
                  <span key={area.areaId}>
                    <span
                      aria-hidden="true"
                      className="live-map__area"
                      style={{
                        clipPath: `polygon(${placed.map((point) => `${point.left.toFixed(2)}% ${point.top.toFixed(2)}%`).join(",")})`,
                      }}
                    />
                    <span
                      className="live-map__pin-label live-map__pin-label--area"
                      style={{
                        left: `${anchor.left.toFixed(2)}%`,
                        top: `${anchor.top.toFixed(2)}%`,
                      }}
                    >
                      {area.name} · GEO-06
                    </span>
                  </span>
                );
              })
            : null}

          {layers.fence
            ? fences.map((fence) => (
                <GeofenceShape
                  key={fence.geofenceId}
                  fence={fence}
                  place={(point) => projection.place(point)}
                  percentPerMetre={projection.percentPerMetre}
                />
              ))
            : null}

          {/*
            The demand pins, on the surface for the first time.

            R-09 recorded this as drawable by no screen: DSP-05 publishes no coordinate, and the
            pickup is behind a per-row MOB-03 read under a scope this page never held. The aggregate
            makes that read, so a dispatch either carries a real `origin` or carries `null` and a
            reason — and a row with no readable pickup draws no pin at all rather than a pin
            somewhere plausible, which is a marker a dispatcher would send a driver to.
          */}
          {layers.demand
            ? queue.map((row) =>
                row.origin === null ? null : (
                  <DemandPin
                    key={row.demandId}
                    row={row}
                    at={projection.place(row.origin)}
                    nowMs={nowMs}
                    selected={selection?.type === "demand" && selection.id === row.demandId}
                    onPick={() => {
                      setSelection({ type: "demand", id: row.demandId });
                    }}
                  />
                ),
              )
            : null}

          {/*
            The job lines and their destination pins, drawn for the first time.

            R-09 recorded these as drawable by no screen and named a route geometry as the
            blocker. Measured against the design rather than against design rule 5's wording,
            that was the wrong blocker: **this line is a rule between two points.** The near one
            is the driver's own fix, which was always published; the far one is
            `tripDestination`, which EXP-008 now joins through TRP-01's trips listing to MOB-03's
            request. No design on this platform draws a road-following route.

            Before the surface has been measured there is no geometry, so nothing is drawn rather
            than a line of zero length rotated to NaN degrees.
          */}
          {layers.jobs && surface.size.width > 0
            ? roster.map((driver) => {
                const to = driver.tripDestination;
                if (driver.fix === undefined || to === undefined) return null;
                return (
                  <JobRoute
                    key={driver.driverId}
                    line={jobLine(projection.place(driver.fix), projection.place(to), surface.size)}
                    at={projection.place(to)}
                    driverId={driver.driverId}
                    selected={selection?.type === "driver" && selection.id === driver.driverId}
                    onPick={() => {
                      setSelection({ type: "driver", id: driver.driverId });
                    }}
                  />
                );
              })
            : null}

          {layers.drivers
            ? roster.map((driver) =>
                driver.fix === undefined ? null : (
                  <DriverMarker
                    key={driver.driverId}
                    driver={driver}
                    at={projection.place(driver.fix)}
                    percentPerMetre={projection.percentPerMetre}
                    selected={selection?.type === "driver" && selection.id === driver.driverId}
                    onPick={() => {
                      setSelection({ type: "driver", id: driver.driverId });
                    }}
                  />
                ),
              )
            : null}
          {/* The surface's own state, over the backdrop rather than instead of it, so a failed read
              does not leave an empty grey rectangle with nothing to say for itself. */}
          {surfaceState.kind === "ready" && surfaceState.stale === false ? null : (
            <div className="live-map__surface-state">
              <StateView
                state={surfaceState}
                purpose="Drivers appear on the map as soon as their devices report a position."
                onRetry={() => {
                  void load();
                }}
              />
            </div>
          )}

          {tiny ? null : card}

          <div
            className={
              selected !== undefined && !tiny
                ? "live-map__legend live-map__legend--shifted"
                : "live-map__legend"
            }
          >
            {LEGEND.map((item) => (
              <span key={item.key} className="live-map__legend-item">
                <span
                  aria-hidden="true"
                  className={`live-map__key live-map__key--${item.key}`}
                  // The two driver keys take their colour from the same table their markers do. A
                  // key drawn in a colour of its own would be a legend that lies the moment either
                  // is changed.
                  style={LEGEND_KEY_STYLES[item.key] ?? {}}
                />
                {item.label}
              </span>
            ))}
          </div>

          {/*
           * The design's note, on the render it describes.
           *
           * `05 Dispatch Live Map.dc.html` writes this word for word and it was true when it was
           * written: no provider had been chosen, the surface was a schematic, and the line told a
           * reader that the emptiness was a decision rather than a gap. ADR 0099 chose Google Maps
           * and the board now draws Nairobi, so on that render the line contradicts what is under
           * it — the only sentence on the screen that does.
           *
           * Shown on the keyless surface, where it is still exactly true, and not over tiles.
           * Removing it outright would delete the design's own words from the render they were
           * written for; leaving it over a map would print something false. Q-256 records the
           * question for the design team either way.
           */}
          {mapProjection === undefined ? (
            <span className="live-map__provider">Map provider open · vendor-neutral surface</span>
          ) : null}
        </div>

        {tiny ? card : null}

        <section className="live-map__roster" aria-label="Driver roster">
          <div className="live-map__roster-head">
            <strong>Driver roster</strong>
            <span className="live-map__roster-count">{roster.length} online</span>
          </div>
          {/*
            The three counts, from the aggregate rather than counted here.

            It counts them before dropping offline drivers, so `counts.offline` says how many were
            excluded — which the design's own roster does not show and this does not draw either.
            The three it draws are the design's three, in its order and its words.
          */}
          <div className="live-map__roster-stats">
            <span className="live-map__stat">
              <span className="live-map__stat-count">{counts?.available ?? 0}</span>
              <span className="live-map__stat-label">available</span>
            </span>
            <span className="live-map__stat">
              <span className="live-map__stat-count">{counts?.job ?? 0}</span>
              <span className="live-map__stat-label">on a job</span>
            </span>
            <span className="live-map__stat">
              <span className="live-map__stat-count">{counts?.stale ?? 0}</span>
              <span className="live-map__stat-label">stale</span>
            </span>
          </div>
          <div className="live-map__roster-rows">
            <StateView
              state={surfaceState}
              purpose="Drivers who are online appear here."
              onRetry={() => {
                void load();
              }}
            >
              {roster.map((driver) => (
                <DriverRow
                  key={driver.driverId}
                  driver={driver}
                  current={selection?.type === "driver" && selection.id === driver.driverId}
                  onPick={() => {
                    setSelection({ type: "driver", id: driver.driverId });
                  }}
                />
              ))}
            </StateView>
          </div>
        </section>
      </div>

      {/*
        What the surface could not read, said rather than left as an absent layer.

        A map missing its geofences looks exactly like a tenant that has drawn none, and an operator
        cannot tell those apart by looking. One label per layer the aggregate reports on, in its
        order, except the drivers' own — that one is already the surface's state.

        `Demand pickups` is the seventh and is new: the pins can now be missing for a reason of
        their own, because the per-row read behind them is under a scope the rest of the page does
        not need. An operator who cannot read MOB-03 sees the queue with no pins on the map, and
        this is the line that says so.
      */}
      <LayerFailures
        failures={[
          { label: "Driver names", state: states.driverProfiles },
          { label: "Driver availability", state: states.driverAvailability },
          { label: "Running trips", state: states.trips },
          { label: "Demand queue", state: states.demand },
          { label: "Demand pickups", state: states.demandOrigins },
          { label: "Service areas", state: states.serviceAreas },
          { label: "Geofences", state: states.geofences },
        ]}
      />
    </section>
  );
}

/* -------------------------------------------------------------------------------------------- */

function Tab({
  tab,
  label,
  count,
  current,
  onPick,
  wide,
}: {
  readonly tab: TabKey;
  readonly label: string;
  readonly count: number;
  readonly current: TabKey;
  readonly onPick: (tab: TabKey) => void;
  readonly wide: boolean;
}): ReactNode {
  // The Drivers tab exists only while the roster does not. Above 1240 the roster is a column of its
  // own, and a tab duplicating it would be two lists of the same drivers side by side.
  if (tab === "drivers" && wide) return null;
  const on = current === tab || (current === "drivers" && wide && tab === "queue");
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      className={on ? "live-map__tab live-map__tab--on" : "live-map__tab"}
      onClick={() => {
        onPick(tab);
      }}
    >
      {label}
      <span className="live-map__tab-count">{count}</span>
    </button>
  );
}

/**
 * DSP-05's five statuses, so an unrecognised sixth can be told from them.
 *
 * `DemandRow.status` is a plain `string` on the aggregate where DSP-05's own listing publishes a
 * five-value enum, and that is the right shape for a BFF: it forwards the owning domain's value
 * rather than re-declaring the domain's vocabulary, so a status DSP-04 adds reaches this screen
 * before this console has a word for it.
 */
const DISPATCH_STATUSES = [
  "finding_candidates",
  "offering",
  "assigning",
  "completed",
  "failed",
] as const;

/**
 * The queue's own word for a dispatch's status, where there is one.
 *
 * An unrecognised value is shown raw rather than relabelled, which is the answer `StateBadge`
 * already gives an unclassified status: an operator reading the published value knows they are
 * looking at something this console has no word for, where one reading a plausible paraphrase does
 * not.
 */
function statusWord(status: string): string {
  return (DISPATCH_STATUSES as readonly string[]).includes(status)
    ? readableStatus(status as (typeof DISPATCH_STATUSES)[number])
    : status;
}

/**
 * Whether a dispatch is drawn as failing rather than as progressing.
 *
 * The design's own rule, in one place because three things read it now: the queue row, the pin on
 * the surface and the selection card. A failed search, or one inside two minutes of its deadline.
 * Both halves come from DSP-05 and travel through the aggregate unchanged.
 */
function failing(row: DemandRowView, nowMs: number): boolean {
  const left = timeLeft(row, nowMs);
  return row.status === "failed" || (left !== undefined && left < 120_000);
}

/** How long a dispatch has left, or nothing when it has no deadline to count. */
function timeLeft(row: DemandRowView, nowMs: number): number | undefined {
  return row.deadlineAt === null ? undefined : Date.parse(row.deadlineAt) - nowMs;
}

function DemandRow({
  row,
  nowMs,
  current,
  onPick,
}: {
  readonly row: DemandRowView;
  readonly nowMs: number;
  readonly current: boolean;
  readonly onPick: () => void;
}): ReactNode {
  const left = timeLeft(row, nowMs);
  const hot = failing(row, nowMs);
  return (
    <button
      type="button"
      aria-current={current ? "true" : undefined}
      className={current ? "live-map__item live-map__item--current" : "live-map__item"}
      onClick={onPick}
    >
      <span
        aria-hidden="true"
        className={hot ? "live-map__item-icon live-map__item-icon--hot" : "live-map__item-icon"}
      >
        pending_actions
      </span>
      <span className="live-map__item-text">
        <span className="live-map__item-top">
          {/*
            The identifier, where the design draws `Westlands → JKIA T1A`.

            No operation on this platform turns a coordinate into a place name — R-08 measured that,
            and it has not changed. The aggregate now carries the pickup *coordinate*, which is what
            puts the pin on the map, and a coordinate is not a place name: the row is still headed
            by what is published.
          */}
          <span className="live-map__item-title">{row.demandId}</span>
          <span
            className={
              hot ? "live-map__item-clock live-map__item-clock--hot" : "live-map__item-clock"
            }
          >
            {row.status === "failed" ? "Exhausted" : left === undefined ? "—" : formatClock(left)}
          </span>
        </span>
        <span className="live-map__item-sub">try {row.attempt}</span>
        <StateBadge
          code="DSP-04"
          status={row.status}
          label={statusWord(row.status)}
          {...(hot ? { classKey: "failed" as const } : {})}
        />
      </span>
    </button>
  );
}

function JobRow({
  job,
  current,
  onPick,
}: {
  readonly job: Job;
  readonly current: boolean;
  readonly onPick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      aria-current={current ? "true" : undefined}
      className={current ? "live-map__item live-map__item--current" : "live-map__item"}
      onClick={onPick}
    >
      <span aria-hidden="true" className="live-map__item-icon live-map__item-icon--job">
        local_taxi
      </span>
      <span className="live-map__item-text">
        <span className="live-map__item-top">
          <span className="live-map__item-title">{job.tripId}</span>
        </span>
        <span className="live-map__item-sub">{job.driverName}</span>
        {/*
          `active` for every job in this tab, because the aggregate reads TRP-02's active trips and
          publishes a trip only as the `tripId` of the driver running it. The two-value lifecycle is
          not lost — it is spent on the question "is this trip running", which the row existing
          answers.
        */}
        <StateBadge code="TRP-02" status="active" classKey="active" />
      </span>
    </button>
  );
}

function DriverRow({
  driver,
  current,
  onPick,
}: {
  readonly driver: RosterDriver;
  readonly current: boolean;
  readonly onPick: () => void;
}): ReactNode {
  const look = DRIVER_MARKERS[markerKeyFor(driver.presence, driver.busyEvidence)];
  const stale = driver.presence === "stale";
  // The identifier rather than a placeholder name. The aggregate leaves `displayName` absent rather
  // than filling it with the identifier — it will not claim DRV-01 published a name it did not —
  // and an operator reading `drv_4410` knows which driver's profile did not arrive, where one
  // reading "Unknown driver" does not.
  const name = driver.displayName ?? driver.driverId;
  return (
    <button
      type="button"
      aria-current={current ? "true" : undefined}
      className={current ? "live-map__driver live-map__driver--current" : "live-map__driver"}
      onClick={onPick}
    >
      <span
        aria-hidden="true"
        className={stale ? "live-map__initials live-map__initials--stale" : "live-map__initials"}
      >
        {initialsOf(name)}
      </span>
      <span className="live-map__driver-text">
        <span className="live-map__driver-name">{name}</span>
        <StateBadge
          code="DRV-05"
          status={driver.presence}
          classKey={look.classKey}
          label={look.word}
          {...(driver.busyEvidence === "undispatchable" ? { title: inferredBusy(driver) } : {})}
        />
      </span>
      <span className={stale ? "live-map__age live-map__age--stale" : "live-map__age"}>
        {driver.fix === undefined ? "—" : formatAge(driver.fix.ageMilliseconds)}
      </span>
    </button>
  );
}

/**
 * Why a badge says a driver is on a job when no trip is running.
 *
 * The same treatment the offer monitor gives a badge whose basis is not in the word itself: the
 * reason belongs on the badge rather than in a column the design does not draw. `reasonCode` is
 * DRV-05's own string, carried verbatim by the aggregate, so this shows the actual reason rather
 * than a paraphrase of it.
 */
function inferredBusy(driver: RosterDriver): string {
  const reason =
    driver.availabilityReason === undefined ? "" : ` with reasonCode ${driver.availabilityReason}`;
  return `Inferred: no trip is running. DRV-05 is withholding this driver from supply${reason}.`;
}

/**
 * A dispatch waiting, on the surface.
 *
 * The design's teardrop: a 26px disc with one square corner, rotated, with the countdown above it
 * and the whole thing anchored at its point rather than at its centre. Violet while the platform is
 * still looking and red once it is failing, which is the same two keys the legend carries — and the
 * tag above it is what keeps the two apart without their colours, because a failing dispatch reads
 * `No driver` where a waiting one reads a clock.
 */
/**
 * One job: a rule from the driver to where they are going, and a pin at the far end.
 *
 * The design draws both in one pass over its `JOBS` list, so they are one component here —
 * separating them would let a surface exist with lines and no pins, which is the one arrangement
 * neither the design nor a dispatcher has any use for.
 *
 * **The line is decoration and the pin is the control.** The design gives the line
 * `pointerEvents:'none'` and puts the click on the marker, which is also the accessible reading:
 * the fact is "this driver's job ends here", and it is announced once by the pin rather than twice.
 *
 * Values copied from the design file rather than from design rule 5's prose, where the two differ.
 * The rule says "solid blue = in trip"; the file draws `rgba(2,106,162,.55)` unselected and the
 * action indigo when selected, over a 3px top border. `ROUTE_LOOKS.in_trip` holds the rule's own
 * colour and stroke so the platform rule stays stated in one place, and the alpha the file applies
 * to an unselected line is applied here.
 */
function JobRoute({
  line,
  at,
  driverId,
  selected,
  onPick,
}: {
  readonly line: JobLine;
  readonly at: { readonly left: number; readonly top: number };
  readonly driverId: string;
  readonly selected: boolean;
  readonly onPick: () => void;
}): ReactNode {
  const look = ROUTE_LOOKS.in_trip;
  return (
    <span>
      <span
        aria-hidden="true"
        className={`live-map__route${selected ? " live-map__route--selected" : ""}`}
        style={{
          left: `${line.left.toFixed(2)}%`,
          top: `${line.top.toFixed(2)}%`,
          width: `${line.length.toFixed(2)}px`,
          transform: `rotate(${line.angle.toFixed(3)}deg)`,
          borderTopStyle: look.stroke,
        }}
      />
      <button
        type="button"
        className={`live-map__destination${selected ? " live-map__destination--selected" : ""}`}
        style={{
          left: `${at.left.toFixed(2)}%`,
          top: `${at.top.toFixed(2)}%`,
          ...(selected ? { zIndex: 9 } : {}),
        }}
        aria-label={`Job destination for driver ${driverId}`}
        onClick={onPick}
      >
        <span aria-hidden="true" className="live-map__destination-glyph">
          location_on
        </span>
      </button>
    </span>
  );
}

function DemandPin({
  row,
  at,
  nowMs,
  selected,
  onPick,
}: {
  readonly row: DemandRowView;
  readonly at: { readonly left: number; readonly top: number };
  readonly nowMs: number;
  readonly selected: boolean;
  readonly onPick: () => void;
}): ReactNode {
  const look = failing(row, nowMs) ? DEMAND_PINS.failing : DEMAND_PINS.waiting;
  const left = timeLeft(row, nowMs);
  return (
    <button
      type="button"
      className={`live-map__pin live-map__pin--${look.key}${selected ? " live-map__pin--selected" : ""}`}
      style={{
        left: `${at.left.toFixed(2)}%`,
        top: `${at.top.toFixed(2)}%`,
        ...(selected ? { zIndex: 9 } : {}),
      }}
      aria-label={`Demand ${row.demandId}, ${statusWord(row.status)}`}
      onClick={onPick}
    >
      <span className="live-map__pin-tag">
        {row.status === "failed" ? DEMAND_EXHAUSTED : left === undefined ? "—" : formatClock(left)}
      </span>
      <span aria-hidden="true" className="live-map__pin-dot" />
    </button>
  );
}

/**
 * A driver on the surface.
 *
 * Design rule 5, drawn: the fill and the glyph come from `DRIVER_MARKERS`, the halo is the rule's
 * blue glow on a job and its accuracy circle when the location is stale, and an offline driver
 * never reaches here because the aggregate has already dropped them.
 *
 * The accuracy circle is sized from the fix's own `horizontalAccuracyMetres` through the
 * projection's scale, so it means what it says: a `±48 m` fix draws a circle 48 metres across on
 * the same ground scale as everything else on the surface.
 *
 * The one marker that is not the design's is the inferred job, whose ring is dashed. It is reached
 * entirely through `markerKeyFor`, so the difference is one table lookup rather than a branch in
 * here — and the `aria-label` says it too, because a dashed ring reaches nobody using a screen
 * reader.
 */
function DriverMarker({
  driver,
  at,
  percentPerMetre,
  selected,
  onPick,
}: {
  readonly driver: RosterDriver;
  readonly at: { readonly left: number; readonly top: number };
  readonly percentPerMetre: number;
  readonly selected: boolean;
  readonly onPick: () => void;
}): ReactNode {
  const key = markerKeyFor(driver.presence, driver.busyEvidence);
  const look = DRIVER_MARKERS[key];
  const accuracy = driver.fix?.horizontalAccuracyMetres ?? 0;
  /*
   * Diameter, not radius: the circle is how wrong the fix may be in any direction.
   *
   * In percent of the surface's width, the unit everything else on it is positioned in, floored so
   * a very precise fix still draws a ring an operator can see rather than a dot inside the marker.
   */
  const halo = look.halo === "accuracy" ? Math.max(4, accuracy * percentPerMetre * 2) : 0;
  const age = driver.fix === undefined ? undefined : formatAge(driver.fix.ageMilliseconds);
  const name = driver.displayName ?? driver.driverId;

  const style: CSSProperties = {
    left: `${at.left.toFixed(2)}%`,
    top: `${at.top.toFixed(2)}%`,
    ...(selected ? { zIndex: 9 } : {}),
  };

  return (
    <button
      type="button"
      className={`live-map__marker live-map__marker--${marker(key)}${selected ? " live-map__marker--selected" : ""}`}
      style={style}
      // The word is the design's; what follows it on an inferred job is the only thing that makes
      // the dashed ring reach somebody who cannot see it.
      aria-label={`Driver ${name}, ${look.word}${key === "job_inferred" ? " (not confirmed)" : ""}`}
      onClick={onPick}
    >
      {look.halo === "accuracy" ? (
        <span
          aria-hidden="true"
          className="live-map__halo live-map__halo--accuracy"
          // A percentage of the map's width rather than a fixed size, because it is a distance on
          // the ground. `aspect-ratio: 1` in the stylesheet makes the height follow, so the circle
          // stays a circle rather than an ellipse when the surface is not square.
          style={{ width: `${halo.toFixed(2)}%` }}
        />
      ) : null}
      {/*
        The fill, the glyph colour and the halo, inline from `DRIVER_MARKERS` and written exactly
        the way the design file writes them. They are the same in both themes — the success and
        info tokens are not — and the stylesheet's own property is that every colour in it is a
        token, so this is where the two colours design rule 5 names belong. `null` for a stale
        driver, whose ring the stylesheet owns because it is `var(--text2)` and follows the theme.
      */}
      <span aria-hidden="true" className="live-map__dot" style={dotStyle(look, selected)}>
        {driverGlyph()}
      </span>
      <span className="live-map__tag">
        {driver.presence === "stale" && age !== undefined ? `${name} · ${age} ago` : name}
      </span>
    </button>
  );
}

/**
 * The stylesheet modifier for one marker look.
 *
 * Two classes rather than five: the four presences the design names, and `--inferred` beside `job`
 * for the fifth. That is what lets the stylesheet say the difference once — a dashed border-style
 * on a ring it has already drawn — instead of restating the whole on-a-job marker.
 */
function marker(key: MarkerKey): string {
  return key === "job_inferred" ? "job live-map__marker--inferred" : key;
}

/**
 * The disc's own colours, for the two states that have one.
 *
 * The design's halo is `rgba(2,106,162,.25)` — the on-a-job blue at a quarter — so it is derived
 * from the fill rather than restated, and a change to the rule's colour cannot leave the halo
 * behind. The selection ring is the design's `rgba(62,99,221,.35)` and comes from `--action`, which
 * is the same value in both themes.
 */
function dotStyle(look: (typeof DRIVER_MARKERS)[MarkerKey], selected: boolean): CSSProperties {
  const ring = "0 0 0 4px color-mix(in srgb, var(--action) 35%, transparent)";
  if (look.fill === null) return selected ? { boxShadow: ring } : {};
  const shadow =
    look.halo === "glow"
      ? `0 0 0 5px color-mix(in srgb, ${look.fill} 25%, transparent), 0 2px 4px rgb(16 24 40 / 30%)`
      : "0 2px 4px rgb(16 24 40 / 30%)";
  return {
    background: look.fill,
    color: look.ink,
    boxShadow: selected ? `${ring}, ${shadow}` : shadow,
  };
}

/**
 * A geofence, drawn as GEO-05 published it.
 *
 * Two shapes, because the contract publishes two: a circle with a centre and a radius in metres,
 * and a polygon of vertices. Both are dashed violet with a name pinned beside them, which is the
 * design's own treatment — and the dash is what keeps the fence distinguishable from the service
 * area when neither colour can be told apart.
 */
function GeofenceShape({
  fence,
  place,
  percentPerMetre,
}: {
  readonly fence: FenceView;
  readonly place: (point: Coordinate) => { left: number; top: number };
  readonly percentPerMetre: number;
}): ReactNode {
  if (fence.shape.kind === "circle") {
    const centre = place(fence.shape.centre);
    const diameter = fence.shape.radiusMetres * percentPerMetre * 2;
    return (
      <>
        <span
          aria-hidden="true"
          className="live-map__fence live-map__fence--circle"
          style={{
            left: `${centre.left.toFixed(2)}%`,
            top: `${centre.top.toFixed(2)}%`,
            width: `${diameter.toFixed(2)}%`,
          }}
        />
        <span
          className="live-map__pin-label live-map__pin-label--fence"
          style={{
            left: `${centre.left.toFixed(2)}%`,
            top: `${(centre.top - diameter / 2).toFixed(2)}%`,
          }}
        >
          {fence.name} · geofence
        </span>
      </>
    );
  }

  const placed = fence.shape.vertices.map((vertex) => place(vertex));
  if (placed.length < 3) return null;
  const anchor = placed.reduce((best, point) => (point.top < best.top ? point : best));
  return (
    <>
      <span
        aria-hidden="true"
        className="live-map__fence live-map__fence--polygon"
        style={{
          clipPath: `polygon(${placed.map((point) => `${point.left.toFixed(2)}% ${point.top.toFixed(2)}%`).join(",")})`,
        }}
      />
      <span
        className="live-map__pin-label live-map__pin-label--fence"
        style={{ left: `${anchor.left.toFixed(2)}%`, top: `${anchor.top.toFixed(2)}%` }}
      >
        {fence.name} · geofence
      </span>
    </>
  );
}

/* -------------------------------------------------------------------------------------------- */
/* The selection card                                                                             */
/* -------------------------------------------------------------------------------------------- */

interface CardRow {
  readonly label: string;
  /** The published field this value came from, in the mono caption the design puts under the label. */
  readonly field: string;
  readonly value: string;
  readonly tone?: "warning" | "absent";
}

interface CardAction {
  readonly label: string;
  /** Why the button cannot be pressed. Every action on this screen has one. */
  readonly why: string;
}

interface CardDetail {
  readonly glyph: string;
  readonly title: string;
  readonly id: string;
  readonly badge: ReactNode;
  readonly rows: readonly CardRow[];
  readonly stale: boolean;
  readonly actions: readonly CardAction[];
}

function useSelection(
  selection: Selection | undefined,
  data: {
    readonly roster: readonly RosterDriver[];
    readonly queue: readonly DemandRowView[];
    readonly jobs: readonly Job[];
    readonly nowMs: number;
  },
): CardDetail | undefined {
  const { roster, queue, jobs, nowMs } = data;
  return useMemo(() => {
    if (selection === undefined) return undefined;

    if (selection.type === "driver") {
      const driver = roster.find((candidate) => candidate.driverId === selection.id);
      if (driver === undefined) return undefined;
      const key = markerKeyFor(driver.presence, driver.busyEvidence);
      const look = DRIVER_MARKERS[key];
      const stale = driver.presence === "stale";
      const rows: CardRow[] = [
        {
          label: "Location",
          field: "fix.fresh",
          value: driver.fix === undefined ? "No fix" : stale ? "Stale" : "Live",
          ...(stale ? { tone: "warning" as const } : {}),
        },
        {
          label: "Last fix",
          field: "fix.ageMilliseconds",
          value: driver.fix === undefined ? "—" : formatLastFix(driver.fix.ageMilliseconds),
        },
        {
          label: "Accuracy",
          field: "fix.horizontalAccuracyMetres",
          value:
            driver.fix === undefined
              ? "—"
              : `±${Math.round(driver.fix.horizontalAccuracyMetres)} m`,
        },
        /*
         * The design draws this row as `Not published · DRV-05 · contract gap G-13`, and G-13 is
         * closed: `GET /v1/driver-availability` publishes `presence`, `availability`, `fresh` and
         * `dispatchable`. So the row carries the value rather than the apology, and the caption
         * carries the field rather than the gap number. R-09 records the change.
         *
         * What the aggregate forwards is DRV-05's `reasonCode`, verbatim and unparsed, rather than
         * the `availability` and `dispatchable` the screen used to read — the two judgements are
         * already spent, on the presence and the evidence this card's badge draws, and repeating
         * them here would be showing the operator the same fact three times. The reason is the part
         * nothing else on the screen can say, and it is also the free-form string Q-12 is about.
         */
        {
          label: "Availability",
          field: "availabilityReason",
          value: driver.availabilityReason ?? "Not read",
          ...(driver.availabilityReason === undefined ? { tone: "absent" as const } : {}),
        },
      ];
      if (driver.tripId !== undefined) {
        rows.push({ label: "Current job", field: "tripId", value: driver.tripId });
      }
      /*
       * The row that exists only when the belief is an inference.
       *
       * A confirmed job has `Current job` above, which is the evidence itself; an inferred one has
       * no trip to name, and this says what there is instead. R-09's over-claim, in words, on the
       * one panel an operator opens before deciding whether to dispatch somebody.
       */
      if (driver.busyEvidence === "undispatchable") {
        rows.push({
          label: "Job evidence",
          field: "busyEvidence",
          value: "Not confirmed",
          tone: "warning",
        });
      }
      return {
        glyph: "person",
        title: driver.displayName ?? driver.driverId,
        id: driver.driverId,
        badge: (
          <StateBadge
            code="DRV-05"
            status={driver.presence}
            classKey={look.classKey}
            label={look.word}
            {...(driver.busyEvidence === "undispatchable" ? { title: inferredBusy(driver) } : {})}
          />
        ),
        rows,
        stale,
        actions:
          driver.presence === "available"
            ? [
                {
                  label: "Assign to a demand",
                  why: "No published operation assigns a hand-picked driver to a demand. See R-08.",
                },
                {
                  label: "Message driver",
                  why: "No masked messaging service exists. Recorded as G-18.",
                },
              ]
            : stale
              ? [
                  {
                    label: "Call driver",
                    why: "No masked call service exists. Recorded as G-18.",
                  },
                  {
                    label: "Location history",
                    why: "DRV-06 publishes GET /v1/drivers/{driverId}/location-observations under driver-location:history:read, which the aggregate does not read and no screen is designed for.",
                  },
                ]
              : [
                  {
                    label: "Open job",
                    why: "This console has no per-trip page to open. Recorded as R-09.",
                  },
                ],
      };
    }

    if (selection.type === "demand") {
      const row = queue.find((candidate) => candidate.demandId === selection.id);
      if (row === undefined) return undefined;
      const left = timeLeft(row, nowMs);
      const hot = failing(row, nowMs);
      const rows: CardRow[] = [
        { label: "Status", field: "status", value: row.status },
        { label: "Attempt", field: "attempt", value: String(row.attempt) },
        {
          label: "Deadline",
          field: "deadlineAt",
          value: left === undefined ? "none" : left <= 0 ? "passed" : `in ${formatClock(left)}`,
        },
        { label: "Failure", field: "failureReason", value: row.failureReason ?? "—" },
      ];
      /*
       * The pickup, and the reason when there is none.
       *
       * New, and it is the row R-09's refusal made impossible: the coordinate is MOB-03's
       * `quote.origin`, reached by the aggregate once per dispatch under a scope this page never
       * held. `originAbsence` is the aggregate saying which of four things happened, and every one
       * of the four is a different thing for an operator to do — ask for a permission, look at a
       * dispatch whose ride request is gone, wait for a service, or scroll up the queue.
       */
      rows.push(
        row.origin === null
          ? {
              label: "Pickup",
              field: "originAbsence",
              value: absentPickup(row.originAbsence),
              tone: "absent",
            }
          : {
              label: "Pickup",
              field: "origin",
              value: `${row.origin.latitude.toFixed(5)}, ${row.origin.longitude.toFixed(5)}`,
            },
      );
      return {
        glyph: "pending_actions",
        title: row.demandId,
        id: row.processId,
        badge: (
          <StateBadge
            code="DSP-04"
            status={row.status}
            label={statusWord(row.status)}
            {...(hot ? { classKey: "failed" as const } : {})}
          />
        ),
        rows,
        stale: false,
        actions: [
          {
            label: "Assign manually",
            why: "No published operation assigns a hand-picked driver to a demand. See R-08.",
          },
          {
            label: "Open in exceptions",
            why: "This console cannot deep-link between pages yet. Recorded as R-09.",
          },
        ],
      };
    }

    const job = jobs.find((candidate) => candidate.tripId === selection.id);
    if (job === undefined) return undefined;
    return {
      glyph: "local_taxi",
      title: job.tripId,
      id: job.driverId,
      badge: <StateBadge code="TRP-02" status="active" classKey="active" />,
      rows: [
        { label: "Type", field: "TRP-02", value: "Trip" },
        { label: "Driver", field: "driverId", value: job.driverName },
        /*
         * `active`, where the design writes `in_trip` and `driver_en_route`.
         *
         * Those are TRP-01's eight-value `status`, and TRP-02's tracking projection deliberately
         * narrows it to two — of which the aggregate reads only the running ones, so this row says
         * the one thing the trip reaching this screen already proves. The join to the eight-value
         * enum exists, keyed on `tripId`, and nothing reads it: not this page, and not EXP-008.
         * R-09.
         */
        { label: "State", field: "tripId on the driver", value: "active" },
      ],
      stale: false,
      actions: [
        { label: "Open job", why: "This console has no per-trip page to open. Recorded as R-09." },
        {
          label: "Reassign",
          why: "DSP-03 releases an assignment and commits a new one, and neither is reachable from this page's scopes. Recorded as R-09.",
        },
      ],
    };
  }, [selection, roster, queue, jobs, nowMs]);
}

/**
 * Why a dispatch has no pickup on it, in the operator's terms rather than the enum's.
 *
 * Four values, four different things to do about them, which is the whole reason the aggregate
 * publishes a reason rather than only a null.
 */
function absentPickup(absence: DemandRowView["originAbsence"]): string {
  switch (absence) {
    case "forbidden":
      return "Needs ride-request:read";
    case "not_found":
      return "No ride request";
    case "unavailable":
      return "MOB-03 did not answer";
    case "not_requested":
      return "Not looked up";
    default:
      return "—";
  }
}

function SelectionCard({
  detail,
  onClose,
}: {
  readonly detail: CardDetail;
  readonly onClose: () => void;
}): ReactNode {
  return (
    <div className="live-map__card" role="dialog" aria-label="Selected">
      <div className="live-map__card-head">
        <span aria-hidden="true" className="live-map__card-glyph">
          {detail.glyph}
        </span>
        <span className="live-map__card-ident">
          <strong className="live-map__card-title">{detail.title}</strong>
          <span className="live-map__card-id">{detail.id}</span>
        </span>
        <button type="button" className="live-map__card-close" aria-label="Close" onClick={onClose}>
          <span aria-hidden="true">close</span>
        </button>
      </div>

      {detail.badge}

      <div className="live-map__fields">
        {detail.rows.map((row) => (
          <div key={row.label} className="live-map__field">
            <span className="live-map__field-text">
              <span className="live-map__field-name">{row.label}</span>
              <span className="live-map__field-source">{row.field}</span>
            </span>
            <span
              className={
                row.tone === undefined
                  ? "live-map__field-value"
                  : `live-map__field-value live-map__field-value--${row.tone}`
              }
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>

      {detail.stale ? (
        <div className="live-map__stale">
          <span className="live-map__stale-title">
            <span aria-hidden="true" className="live-map__stale-glyph">
              signal_disconnected
            </span>
            Location is stale
          </span>
          <span className="live-map__stale-body">
            Shown where it last was, not where it might be now. New offers are paused until a fresh
            fix arrives.
          </span>
        </div>
      ) : null}

      <div className="live-map__actions">
        {detail.actions.map((action, index) => (
          <button
            key={action.label}
            type="button"
            className={
              index === 0 ? "live-map__action live-map__action--primary" : "live-map__action"
            }
            // Drawn, and refused. Every command this card offers either has no published operation
            // or needs a scope this page does not hold, and a live button would mean inventing a
            // payload or producing a 403 the operator cannot act on.
            disabled
            title={action.why}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function LayerFailures({
  failures,
}: {
  readonly failures: readonly { readonly label: string; readonly state?: ViewState | undefined }[];
}): ReactNode {
  const lost = failures.filter((entry) => entry.state !== undefined);
  if (lost.length === 0) return null;
  return (
    <div className="live-map__lost" role="status">
      <span aria-hidden="true" className="live-map__lost-glyph">
        layers_clear
      </span>
      <span>
        Not on this map: {lost.map((entry) => entry.label).join(", ")}. What you can see is
        everything the other reads answered.
      </span>
    </div>
  );
}

/** The two-letter avatar the roster draws, from whatever shape the published name has. */
function initialsOf(name: string): string {
  const letters = name
    .split(/\s+/)
    .map((part) => part.replace(/[^A-Za-z0-9]/g, "").charAt(0))
    .filter((letter) => letter.length > 0);
  return (letters.slice(0, 2).join("") || name.slice(0, 2)).toUpperCase();
}
